import {
  Battery,
  Megaphone,
  PersonStanding,
  Sparkles,
  Video,
  Wrench,
  Zap,
} from 'lucide-react';

import { cls } from '@/utils/cls';
import { formatLocalDateTime } from '@/utils/format/date';
import { getUserTimezone } from '@/utils/timezone';

import { newsStyles } from '../../styles';
import type { News } from '../../types';
import { WhatsNewLink } from '../../whats-new-link';

import { ExternalLink } from './external-link';

export const getGeneralNews = (): News[] => {
  const styles = newsStyles();
  const userTimezone = getUserTimezone();

  return [
    {
      key: 'webinar-overlooked-battery-data',
      icon: <Video className={styles.icon()} />,
      expiredAt: new Date('2025-11-20'),
      isCollapsible: false,
      label: (
        <>
          <h2>
            <ExternalLink
              href="https://www.accure.net/events/webinar-turning-overlooked-battery-data-into-an-asset-management-advantage"
              className="mb-2"
            >
              Webinar: How To Turn Overlooked Battery Data Into An Asset
              Management Advantage
            </ExternalLink>
          </h2>
          <p>
            Don’t miss this expert session - see how unlocking battery
            performance with overlooked data can increase capacity by 6%. Find
            out how asset managers and energy leaders drive results using
            analytics to boost reliability and reduce risk.
          </p>
          <br />
          <p>
            📅 <strong>Wednesday, November 19, 2025</strong> <br />🕒{' '}
            <strong>3–4pm GMT | 10–11am EST</strong>
          </p>
          <br />
          <p>
            <ExternalLink href="https://www.accure.net/events/webinar-turning-overlooked-battery-data-into-an-asset-management-advantage">
              👉 Register now
            </ExternalLink>
          </p>
        </>
      ),
      thumbnail: {
        src: 'https://cdn.prod.website-files.com/67f3b2b2af7509d6ee12765a/690482e41c9b49181606a58b_Version%202-1%20(1).png',
        alt: 'Webinar: How To Turn Overlooked Battery Data Into An Asset Management Advantage',
        href: 'https://www.accure.net/events/webinar-turning-overlooked-battery-data-into-an-asset-management-advantage',
      },
    },
    {
      key: 'maintainance-reminder-2025-04-12',
      priority: 5, // High priority for maintenance reminder news
      icon: <Wrench className="size-full text-amber-700" />,
      expiredAt: new Date('2025-04-12'),
      isCollapsible: false,
      label: (
        <>
          <a
            className="font-semibold text-amber-700"
            target="_blank"
            rel="noreferrer"
          >
            Scheduled Maintenance Notice: Saturday,{' '}
            {formatLocalDateTime(
              new Date('2025-04-12T20:30:00+02:00'),
              userTimezone,
            )}
          </a>
          <p>
            We're performing a quick maintenance to keep things running
            smoothly. Expect a short downtime on the day. Don't worry — it'll be
            over in a flash, and we'll be back better than ever! Thanks for your
            patience!
          </p>
        </>
      ),
    },
    {
      key: 'energy-storage-summit-australia-2025',
      icon: <Zap className={cls(styles.icon(), 'text-yellow-300')} />,
      expiredAt: new Date('2025-05-09'),
      label: (
        <>
          <a
            href="https://info.accure.net/the-smarter-e-europe-customer-event"
            className="mb-2 font-semibold text-teal-700 hover:text-teal-800 hover:underline"
            target="_blank"
            rel="noreferrer"
          >
            Exclusive Invite: Connect with Us!
          </a>
          <p>
            Join us at Europe's leading energy exhibition,{' '}
            <strong>The smarter E Europe / Intersolar 2025</strong>, as we
            collectively accelerate towards a smarter, integrated energy future.
          </p>
          <p>
            🤝 <strong>Special Opportunity:</strong> We're offering our valued
            users an exclusive chance to meet our team in person—including an{' '}
            <strong>exciting brewery tour & beer tasting in Munich</strong>!
            Let's explore synergies, exchange ideas, and collaborate toward
            groundbreaking energy solutions.
          </p>
          <p>
            📅 <strong>When:</strong> May 7-9, 2025
          </p>
          <p>
            📍 <strong>Where:</strong> Messe München, Munich, Germany, Hall B2,
            Booth 386
          </p>
          <p>
            🎟️ <strong>Need a ticket?</strong> We've got you covered! Simply
            reach out and we'll arrange one for you.
          </p>

          <p>
            {' '}
            <a
              href="https://info.accure.net/the-smarter-e-europe-customer-event"
              className="font-semibold text-teal-700 hover:text-teal-800 hover:underline"
              target="_blank"
              rel="noreferrer"
            >
              👉 Learn more and connect with us here
            </a>
            !
          </p>
        </>
      ),
      thumbnail: {
        src: 'https://cdn.prod.website-files.com/62dfa1f78c33b8d75c98ec7a/678a06fb3ea53467daf6def3_Intersolar_2025.jpg',
        alt: 'The smarter E Europe / Intersolar 2025',
        href: 'https://www.accure.net/events/the-smarter-e-europe',
      },
    },
    {
      key: 'energy-storage-summit-australia-2025',
      icon: <PersonStanding className="size-full text-teal-700" />,
      expiredAt: new Date('2025-03-19'),
      label: (
        <>
          <a
            href="https://www.accure.net/events/energy-storage-summit-australia"
            className="mb-2 font-semibold text-teal-700 hover:text-teal-800 hover:underline"
            target="_blank"
            rel="noreferrer"
          >
            Energy Storage Summit Australia 2025 🌏
          </a>
          <p>
            Join our very own Commercial Director,{' '}
            <strong>Steve Higgins</strong>, in his session{' '}
            <q>
              Maintaining safety and profitability using predictive battery
              analytics
            </q>
            .
          </p>
          <p>
            Steve’s covering how predictive analytics are shaking things up for
            BESS operations—spotting risks before they happen, minimizing
            unplanned downtimes, and helping operators make smarter, more
            informed decisions. He’s even bringing real-world examples to show
            how this tech is improving both safety and revenue!
          </p>
          <p>Make sure to drop by the session and soak up the insights:</p>
          <p>
            📍 Sofitel Sydney Wentworth, Sydney <br />📅 March 18, 2025 <br />🕐
            4.15 pm (AEDT)
          </p>
          <p>
            👉 Book your meeting{' '}
            <a
              href="https://hubs.ly/Q039D_2W0"
              className="font-semibold text-teal-700 hover:text-teal-800 hover:underline"
              target="_blank"
              rel="noreferrer"
            >
              here
            </a>
            !
          </p>
        </>
      ),
      thumbnail: {
        src: 'https://cdn.prod.website-files.com/62dfa1f78c33b8d75c98ec7a/6761652a445aedd54588305f_53754706476_573512e70f_c.jpg',
        alt: 'Energy Storage Summit Europe 2025',
        href: 'https://www.accure.net/events/energy-storage-summit-australia',
      },
    },
    {
      key: 'series-b-funding-announcement',
      icon: <Megaphone className={styles.icon()} />,
      expiredAt: new Date('2025-03-15'),
      label: (
        <>
          <h2>
            <ExternalLink
              href="https://www.linkedin.com/posts/accure-battery-intelligence_we-have-two-very-bigand-very-excitingannouncements-activity-7295445146890895360-rkpShttps://www.accure.net/battery-knowledge/accure-battery-intelligence-secures-16-million-to-scale-battery-safety-and-performance-offerings-across-europe-americas-and-asia-pacific"
              className="mb-2"
            >
              ACCURE has secured $16M in Series B funding!
            </ExternalLink>
          </h2>
          <p>
            We have a big — and exciting — announcement!
            <br />
            <br />
            This milestone, supported by incredible capital partners: Incharge
            Capital Partners, Blue Bear Capital, HSBC Asset Management,
            Riverstone Holdings, Capnamic, and 42CAP, accelerates our ability to
            scale our predictive battery analytics platform globally and further
            enhance its capabilities. As we expand, we are focused on
            continuously advancing the platform's functionality, ensuring our
            customers gain even more valuable insights for smart
            decision-making.
            <br />
            <br />
            Together, we're ensuring that energy storage systems and EV fleets
            are safer, more reliable, and performing at their peak. This funding
            empowers us to bring AI-driven battery intelligence to operators
            across Europe, the Americas, and the Asia Pacific, helping drive the
            world's transition to clean energy.
          </p>
        </>
      ),
    },
    {
      key: 'stay-ahead-of-bess-failures-with-predictive-analytics',
      icon: <PersonStanding className={styles.icon()} />,
      expiredAt: new Date('2025-02-26'),
      label: (
        <>
          <h2>
            <ExternalLink href="https://hubs.ly/Q035zYY00" className="mb-2">
              Prevent BESS Failures with Predictive Analytics
            </ExternalLink>
          </h2>
          <p>
            Join experts{' '}
            <ExternalLink href="https://www.linkedin.com/in/marc-mennekes/">
              Marc Mennekes
            </ExternalLink>{' '}
            and{' '}
            <ExternalLink href="https://www.linkedin.com/in/timo-gollub/">
              Timo Gollub
            </ExternalLink>{' '}
            to learn how predictive analytics can prevent battery failures and
            improve system reliability. Discover common failure causes and
            real-world success stories. Essential for BESS stakeholders.
            <br />
            <br />
            🗓️ Wednesday, February 26 <br />🕓 16:00 PM (CET) | 10:00 AM (EST)
            <br />
            <br />
            <ExternalLink href="https://hubs.ly/Q035zYY00">
              Register Now
            </ExternalLink>
          </p>
        </>
      ),
      thumbnail: {
        src: 'https://media.licdn.com/dms/image/v2/D4E10AQHuc5I1BPkB-A/image-shrink_1280/B4EZTb0rpHHcAQ-/0/1738854809288?e=2147483647&v=beta&t=OsT6hmWUtFsVUPFgjX_wAkdASmr0rOtzZ3n-qzU-zAE',
        alt: 'Energy Storage Summit Europe 2025',
        href: 'https://hubs.ly/Q035zYY00',
      },
    },
    {
      key: 'new-year-greetings-2024',
      priority: 1, // Adjust priority as needed
      icon: <Battery className={styles.icon()} />, // Use an appropriate icon
      expiredAt: new Date('2025-01-15'), // Expiry date for the message
      label: (
        <>
          <span className={styles.primary()}>
            🎉 Happy New Year from ACCURE! 🌟
          </span>
          <br />
          As we step into <strong>2025</strong>, we’re energized to power a
          brighter, more sustainable future together! Thank you for being part
          of the journey in advancing innovation in batteries and the energy
          sector. Here’s to a year filled with new possibilities and recharged
          ambitions! 💡⚡
          <br />
          Want to stay updated? Visit our{' '}
          <ExternalLink href="https://de.linkedin.com/company/accure-battery-intelligence">
            LinkedIn page
          </ExternalLink>{' '}
          for more!
        </>
      ),
    },
    {
      key: 'energy-storage-summit-europe-2025',
      icon: <PersonStanding className={styles.icon()} />,
      expiredAt: new Date('2025-02-19'),
      label: (
        <>
          <ExternalLink
            href="https://storagesummit.solarenergyevents.com/seminar/improving-performance-increasing-profitability-of-bess/"
            className="mb-2"
          >
            Energy Storage Summit Europe 2025
          </ExternalLink>
          <br />
          Come and see ACCURE co-founder and CEO, Dr. Kai-Philipp Kairies, as he
          joins a panel of industry experts to discuss improving the performance
          and profitability of Battery Energy Storage Systems.
          <br />
          <br />
          🗓️ Tuesday, February 18 <br />🕓 14:00 PM (GMT)
          <br />
          <br />
          Make sure to get in touch and arrange a meeting with us.
          <br />
        </>
      ),
      thumbnail: {
        src: 'https://cdn.prod.website-files.com/62dfa1f78c33b8d75c98ec7a/67616801445aedd5458ab6c1_53545719364_53df171429_c.jpg',
        alt: 'Energy Storage Summit Europe 2025',
        href: 'https://storagesummit.solarenergyevents.com/seminar/improving-performance-increasing-profitability-of-bess/',
      },
    },
    {
      key: 'solarplaza-asset-management-summit-europe',
      icon: <PersonStanding className={styles.icon()} />,
      expiredAt: new Date('2024-10-16'),
      label: (
        <>
          <ExternalLink href="https://www.accure.net/events/solarplaza-asset-management-summit-europe">
            Solarplaza Asset Management Summit Europe
          </ExternalLink>
          <br />
          Connect with solar energy professionals and industry leaders at this
          premier event to discuss strategies, technologies, and solutions for
          effective solar asset management.
        </>
      ),
      thumbnail: {
        src: 'https://cdn.prod.website-files.com/62dfa1f78c33b8d75c98ec7a/66fbf1994f55d0a217da1f9a_52435454133_dcd0f4bbfe_o.jpg',
        alt: 'Solarplaza Asset Management Summit Europe',
        href: 'https://www.accure.net/events/solarplaza-asset-management-summit-europe',
      },
    },
    {
      key: 'battery-asset-management-summit-2024',
      icon: <PersonStanding className={styles.icon()} />,
      expiredAt: new Date('2024-11-13'),
      label: (
        <>
          <ExternalLink href="https://www.accure.net/events/battery-asset-management-summit-2024">
            Battery Asset Management Summit 2024
          </ExternalLink>
          <br />
          Join industry leaders and experts, including Accure's distinguished
          team members, in impactful discussions on the future of battery
          technology and lifecycle management.
        </>
      ),
      thumbnail: {
        src: 'https://cdn.prod.website-files.com/62dfa1f78c33b8d75c98ec7a/66a39e997a57db5e55c405f6_battery%20asset%20management%20image.jpeg',
        alt: 'Battery Asset Management Summit 2024',
        href: 'https://www.accure.net/events/battery-asset-management-summit-2024',
      },
    },
    {
      key: 'battery-day-2024',
      icon: <PersonStanding className={styles.icon()} />,
      expiredAt: new Date('2024-09-25'),
      label: (
        <>
          <ExternalLink href="https://www.accure.net/events/battery-day-2024---the-hague">
            Battery Day 2024
          </ExternalLink>{' '}
          inspires, informs, and ignites meaningful conversations that transcend
          boundaries and drive tangible impact.
          <br />
          <br />
          Meet our Head of Custom Analytics,{' '}
          <ExternalLink href="https://www.linkedin.com/in/darya-chahardahcherik-075380175/">
            Darya Chahardahcherik
          </ExternalLink>
          , in the Battery Day session "1.4 Battery management & data" on
          September 24, 11:40 AM - 13:00 PM
        </>
      ),
      thumbnail: {
        src: 'https://cdn.prod.website-files.com/62dfa1f78c33b8d75c98ec7a/66d7359eaeabb8e4430da262_6667a7934e9f6518312a97c83987c357.jpg',
        alt: '',
        href: 'https://www.accure.net/events/battery-day-2024---the-hague',
      },
    },
    {
      key: 're-plus-2024',
      icon: <PersonStanding className={styles.icon()} />,
      expiredAt: new Date('2024-09-13'),
      label: (
        <>
          Join us in Anaheim from September 9-12 for{' '}
          <ExternalLink href="https://www.re-plus.com/">RE+</ExternalLink>,
          North America’s largest renewable show. <br /> Experts from ACCURE
          will be sharing their battery knowledge during several sessions, and
          we’ll be hosting guests at our booth. Please get in touch if you would
          like to meet at the event.
        </>
      ),
      thumbnail: {
        src: 'https://www.re-plus.com/wp-content/uploads/2023/06/RE2022M1632-1024x683-1.jpg',
        alt: '',
        href: 'https://www.re-plus.com',
      },
    },
    {
      key: 'changelog',
      icon: <Sparkles className="size-full text-red-600" />,
      expiredAt: new Date('2024-03-12'),
      label: (
        <>
          New <WhatsNewLink to="/changelog">functions</WhatsNewLink> have been
          released.
        </>
      ),
    },
    {
      key: 'webinar',
      icon: <Video className={styles.icon()} />,
      expiredAt: new Date('2024-01-01'),
      label: (
        <>
          We’ve got a new webinar live, take a look{' '}
          <ExternalLink href="https://youtube.com">here</ExternalLink>.
        </>
      ),
    },
  ];
};
